/**
 * Every page should have a Camel Case name
 */
export const enum PageName {
  ACCEPT_INVITATION = 'Accept Invitation',
  ACCOUNT_SETTINGS = 'Account Settings',
  AI_AGENTS = 'AI Agents',
  AI_AGENT_GUIDELINES = 'AI Agent Guidelines',
  AI_AGENT_OVERVIEW = 'AI Agent Overview',
  AI_AGENT_SETTINGS = 'AI Agent Settings',
  API_KEYS = 'API Keys',
  ANALYTICS_OVERVIEW = 'Analytics Overview',
  ANALYTICS_USAGE = 'Analytics Usage',
  ANALYTICS_BRAIN_EFFECTIVENESS = 'Analytics Brain Effectiveness',
  ANALYTICS_HUMAN_EFECTIVENESS = 'Analytics Human Agent Effectiveness',
  ANALYTICS_HUMAN_AGENTS = 'Analytics Human Agents',
  ANALYTICS_AI_AGENTS = 'Analytics AI Agents',
  ANALYTICS_AGENTS_PERFORMANCE = 'Analytics Agents Performance',
  ANALYTICS_ACCOUNT_CHAT_LOGS = 'Analytics Account Chat Logs',
  ANALYTICS_CUSTOMER_SATISFACTION = 'Analytics Customer Satisfaction',
  ANALYTICS_CUSTOM_REPORTS = 'Analytics Custom Reports',
  ANALYTICS_CUSTOM_REPORT_DETAIL = 'Analytics Custom Report Detail',
  ANALYTICS_USER_FEEDBACK = 'Analytics User Feedback',
  BILLING = 'Billing',
  BRAIN_LOGS = 'Brain Logs',
  BROADCAST = 'Broadcast',
  BROADCASTS = 'Broadcasts',
  BUNDLE = 'Bundle',
  BUNDLES = 'Bundles',
  BUSINESS_HOUR = 'Business Hour',
  BUSINESS_HOURS = 'Business Hours',
  CHATS = 'Chats',
  COLLECTION = 'Collection',
  COLLECTIONS = 'Collections',
  CONTACT = 'Contact',
  CONTACTS = 'Contacts',
  CONVERSATION = 'Conversation',
  CONVERSATIONS = 'Conversations',
  CREATE_ACCOUNT = 'Create Account',
  DATASOURCE = 'Datasource',
  DATA_PROCESS = 'Data Process',
  DEBTS = 'Debts',
  DEPARTMENTS = 'Departments',
  DESK_SETTINGS = 'Desk Settings',
  DIALOG = 'Dialog',
  ENTITIES = 'Entities',
  ENTITY = 'Entity',
  ERROR_500 = 'Error_500',
  HOME = 'Home',
  INSIGHTS = 'Insights',
  INTEGRATION = 'Integration',
  INTEGRATIONS = 'Integrations',
  INTENT = 'Intent',
  INTENTS = 'Intents',
  INVOICES = 'Invoices',
  USAGE = 'Usage',
  LANGUAGE_MODELS = 'Language Models',
  MCPS = 'MCPs',
  MCP_SERVER = 'MCP Server',
  MANAGE_MEMBER_ACCESS = 'Manage Member Access',
  MANAGE_ROLE_ACCESS = 'Manage Role Access',
  MANAGE_ROLE_PERMISSIONS = 'Manage Role Permissions',
  MANAGE_TEAM_ACCESS = 'Manage Team Access',
  NOT_FOUND = 'Not Found',
  ONBOARDING_0 = 'Onboarding - AI Agent type',
  ONBOARDING_1 = 'Onboarding - AI Agent settings',
  ONBOARDING_2 = 'Onboarding - Connect Collection',
  ONBOARDING_3 = 'Onboarding - Create Dialogs',
  PERSONAL_INVITATIONS = 'Personal Invitations',
  PERSONAL_NOTIFICATIONS = 'Personal Notifications',
  PERSONAL_PROFILE = 'Personal Profile',
  PERSONAL_SECURITY = 'Personal Security',
  RULE = 'Rule',
  RULES = 'Rules',
  UNAUTHORIZED = 'Unauthorized',
  VERSIONS = 'Versions',
  WEBHOOK = 'Webhook',
  WEBHOOKS = 'Webhooks',
  WELCOME = 'Welcome',

  // Campaigns
  CAMPAIGNS = 'Campaigns',
  CAMPAIGN_SETUP = 'Campaign Setup',
  CAMPAIGN_MESSAGING = 'Campaign Messaging',
  CAMPAIGN_REVIEW = 'Campaign Review',
  CAMPAIGN_LOGS = 'Campaign Logs',
  CAMPAIGN_ANALYTICS = 'Campaign Analytics',
  CAMPAIGN_PERFORMANCE = 'Campaign Performance',
  CAMPAIGN_SUBSCRIBER_JOURNEY = 'Campaign Subscriber Journey',

  // Simulations
  SIMULATIONS = 'Simulations',
  SIMULATIONS_OVERVIEW = 'Simulations Overview',
  SIMULATION_RUN = 'Simulation Run',

  // Improvements
  IMPROVEMENTS = 'Improvements',
}

/**
 * Amplitude page taxonomy vocabulary.
 *
 * These two low-cardinality unions describe every page-view event:
 * `PageSection` = the product's top-level nav area, `PageType` = the shape of
 * the page. They live here (next to `PageName` and `EventProps`) so the shared
 * types layer owns the vocabulary and there is no client<->shared import cycle.
 * The `PageName -> { section, type }` mapping itself lives in
 * `src/client/segment/pageTaxonomy.ts`.
 */
export type PageSection =
  | 'home'
  | 'ai-agents'
  | 'analytics'
  | 'knowledge-bases'
  | 'chats'
  | 'contacts'
  | 'campaigns'
  | 'broadcasts'
  | 'integrations'
  | 'simulations'
  | 'improvements'
  | 'settings'
  | 'billing'
  | 'onboarding'
  | 'auth'
  | 'system';

export type PageType =
  'list' | 'detail' | 'editor' | 'settings' | 'analytics' | 'wizard' | 'auth' | 'other';

/**
 * The event names to send to Segment. Make sure you pick a category. Either
 * * Click - when a user clicks on something
 * * View - when a user views something
 * * Run - when a user runs a process
 * * Open - when a user opens something like a menu or modal
 * * Select - when a user selects something from a dropdown
 */

export enum EventName {
  AutoDialogCompleted = 'AutoDialog Completed',
  AutoDialogError = 'AutoDialog Error',
  ToggleTrainingPhrasePanel = 'Toggle Training Phrase Panel',
  ClickAnalyticsLogsExport = 'Click Analytics Logs Export',
  ClickBroadCastSchedule = 'Click Broadcast Schedule',
  ClickBroadCastSendNow = 'Click Broadcast Send Now',
  ClickCopyBrainId = 'Click Copy Brain Id',
  ClickCopyDatasourceId = 'Click Copy Datasource Id',
  ClickCopyDocumentId = 'Click Copy Document Id',
  ClickCreateAIAgent = 'Click Create AI Agent',
  ClickCreateAccessTeam = 'Click Create Access Team',
  ClickCreateAccount = 'Click Create Account',
  ClickCreateBrain = 'Click Create Brain',
  ClickCreateBroadcast = 'Click Create Broadcast',
  ClickCreateManualDialog = 'Click Create Manual Dialog',
  ClickDeleteBrain = 'Click Delete Brain',
  ClickDeleteInvitation = 'Click Delete Invitation',
  ClickDeleteMember = 'Click Delete Member',
  ClickDeleteWebhook = 'Click Delete Webhook',
  ClickDocumentation = 'Click Documentation',
  ClickDownloadDocument = 'Click Download Document',
  ClickEditAccessTeam = 'Click Edit Access Team',
  ClickEditMember = 'Click Edit Member',
  ClickEditWebhook = 'Click Edit Webhook',
  ClickEmailNotification = 'Toggle Weekly Email',
  ClickExportBrain = 'Click Export Brain',
  ClickExportWebhook = 'Click Export Webhook',
  ClickImportWebhook = 'Click Import Webhook',
  ClickPlatformStatus = 'Click Platform Status',
  ClickRevokeAllWebSessions = 'Click Revoke All Web Sessions',
  ClickRevokeWebSession = 'Click Revoke Web Session',
  ClickSubmitAutoDialogPrompt = 'Click Submit Auto Dialog Prompt',
  ClickTransferAccount = 'Click Transfer Account',
  ClickTryIt = 'Click Try It',
  ClickUpgradePlan = 'Click Upgrade Plan',
  CreateView = 'Click Create View',
  DeleteView = 'Click Delete View',
  FilterByView = 'Select View',
  OpenHelpMenu = 'Open Help Menu',
  OpenUserMenu = 'Open User Menu',
  RunAnalyticsExport = 'Run Analytics Export',
  // Analytics adoption / engagement instrumentation
  ApplyAnalyticsFilter = 'Apply Analytics Filter',
  ToggleAnalyticsCompare = 'Toggle Analytics Compare',
  ClickCreateReport = 'Click Create Report',
  SelectAccount = 'Select Account',
  SendTryItMessage = 'Send Try It Message',
  UpdateView = 'Click Update View',
  UploadBroadcastFile = 'Upload Broadcast File',

  // Onboarding
  OnboardingClickAcceptInvitation = 'Click Accept Invitation',
  OnboardingClickDeclineInvitation = 'Click Decline Invitation',
  OnboardingKnowledgeWebsite = 'Onboarding Website Added',
  OnboardingKnowledgeFiles = 'Onboarding Files Added',
  OnboardingCancelled = 'Onboarding Cancelled',
  OnboardingCompleted = 'Onboarding Completed',
  OnboardingStepCompleted = 'Onboarding Step Completed',
  OnboardingStepViewed = 'Onboarding Step Viewed',
  OnboardingSkipped = 'Onboarding Skipped',
  OnboardingTemplateModified = 'Onboarding Template Modified',

  // Conversation Summarization
  ClickConversationSummarize = 'Click Conversation Summarize',
  ConversationSummarizeSuccess = 'Conversation Summarize Success',
  ConversationSummarizeError = 'Conversation Summarize Error',
  ConversationSummarizeNoNewMessages = 'Conversation Summarize No New Messages',

  // Conversation Translation
  ClickConversationTranslate = 'Click Conversation Translate',
  ConversationTranslateSuccess = 'Conversation Translate Success',
  ConversationTranslateError = 'Conversation Translate Error',

  // API Keys
  ClickGenerateAPIKey = 'Click Generate API Key',
  ClickDeleteAPIKey = 'Click Delete API Key',
  ClickCopyAPIKey = 'Click Copy API Key',

  // New Message (Outbound)
  ClickNewMessageBottomBanner = 'Click New Message Bottom Banner',
  ClickNewMessageNext = 'Click New Message Next',
  ClickNewMessageSend = 'Click New Message Send',
  ClickNewMessageSquarePen = 'Click New Message Square Pen',
  NewMessageSendError = 'New Message Send Error',
  NewMessageSendSuccess = 'New Message Send Success',
  SelectNewMessageIntegration = 'Select New Message Integration',

  // Billing
  ChangePlan = 'Change Plan',
  CancelPlan = 'Cancel Plan',
  ResumePlan = 'Resume Plan',
  ContactSales = 'Contact Sales',

  // Command Palette
  OpenCommandPalette = 'Open Command Palette',
  CommandPaletteSelect = 'Command Palette Select',
  CommandPaletteDrillDown = 'Command Palette Drill Down',
  CommandPaletteSearch = 'Command Palette Search',

  // Campaigns
  ClickCreateCampaign = 'Click Create Campaign',
  CampaignSetupContinue = 'Campaign Setup Continue',
  CampaignSetupSaveAndExit = 'Campaign Setup Save And Exit',
  CampaignMessagingContinue = 'Campaign Messaging Continue',
  CampaignMessagingSaveAndExit = 'Campaign Messaging Save And Exit',
  CampaignReviewAndStart = 'Campaign Review And Start',
  CampaignConfirmLaunch = 'Campaign Confirm Launch',
  ClickStopCampaign = 'Click Stop Campaign',
  ConfirmStopCampaign = 'Confirm Stop Campaign',
  ClickDeleteCampaign = 'Click Delete Campaign',
  ConfirmDeleteCampaign = 'Confirm Delete Campaign',
  ClickBulkDeleteCampaigns = 'Click Bulk Delete Campaigns',
  ConfirmBulkDeleteCampaigns = 'Confirm Bulk Delete Campaigns',
  ClickEditCampaign = 'Click Edit Campaign',
  CampaignDownloadSample = 'Campaign Download Sample',
  CampaignAddChannel = 'Campaign Add Channel',
  CampaignRemoveChannel = 'Campaign Remove Channel',
  CampaignConfigureNBA = 'Campaign Configure NBA',
  CampaignToggleSchedule = 'Campaign Toggle Schedule',
  CampaignLogsFilter = 'Campaign Logs Filter',
  CampaignLogsClickRow = 'Campaign Logs Click Row',
  CampaignJourneyNavigate = 'Campaign Journey Navigate',
  CampaignStopSubscriber = 'Campaign Stop Subscriber',
  CampaignResumeSubscriber = 'Campaign Resume Subscriber',

  // Simulations
  ClickCreateSimulation = 'Click Create Simulation',
  RunSimulations = 'Run Simulations',

  // Home activation checklist. One event with a bounded `checklist_step`, so a
  // step added to the checklist cannot silently stop being tracked the way the
  // per-step `EventName[key]` lookup this replaces did.
  ChecklistStepCompleted = 'Checklist Step Completed',

  ModalOpened = 'Modal Opened',
  ActionFailed = 'Action Failed',
  CampaignLaunched = 'Campaign Launched',
  InvitationAccepted = 'Invitation Accepted',
  PageSectionExited = 'Page Section Exited',
  ViewCreated = 'View Created',
  ViewUpdated = 'View Updated',
  ViewDeleted = 'View Deleted',
  AIAgentCreated = 'AI Agent Created',
  SimulationCreated = 'Simulation Created',
  SimulationRunStarted = 'Simulation Run Started',
  CampaignCreated = 'Campaign Created',
  RuleCreated = 'Rule Created',
  RuleDuplicated = 'Rule Duplicated',
  RuleUpdated = 'Rule Updated',

  // Chat Notes
  OpenNoteComposer = 'Open Note Composer',
  NoteAdded = 'Note Added',
  TrainingPhraseAdded = 'Training Phrase Added',
}

/** Why an action failed. Map with `toErrorReason`. */
export type ErrorReason =
  | 'network'
  | 'timeout'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'rate_limited'
  | 'validation'
  | 'server'
  | 'unknown';

/**
 * The verb an outcome pair is joined on: the same value rides on the success
 * event's mutation meta and on its `Action Failed`. Closed, because a typo here
 * splits a funnel in Amplitude and nothing else would catch it.
 */
export type SegmentAction =
  | 'ai_agent_create'
  | 'campaign_create'
  | 'campaign_launch'
  | 'invitation_accept'
  | 'note_create'
  | 'rule_create'
  | 'rule_duplicate'
  | 'rule_update'
  | 'simulation_create'
  | 'simulation_run'
  | 'view_create'
  | 'view_delete'
  | 'view_update';

/**
 * The member's standing in the account they are working in. One bounded value,
 * so it survives an Amplitude group-by: a member holding several roles is
 * reported as the most privileged one. An account's own roles are reported as
 * `custom` rather than by name — the names identify the account and the ids are
 * per-account — and so are the retired `Reader` and `Writer`. `none` is a member
 * holding no role at all.
 */
export type UserRole =
  'owner' | 'admin' | 'builder' | 'chat_manager' | 'chat_agent' | 'billing' | 'custom' | 'none';

export type EventProps = Partial<{
  brain_id: string;
  webhook_id: string;
  session_id: string;
  collection_id: string;
  broadcast_id: string;
  datasource_id: string;
  document_id: string;
  title: string;
  type: string;
  brain_type: string;
  weekly_email: boolean;
  step: string | number;
  route: string;
  video_url: string;
  number: number;
  language: string;
  // Onboarding tracking
  files_count: number;
  has_website: boolean;
  skipped: boolean;
  modified_fields: string[];
  // New Message (Outbound) tracking
  has_template: boolean;
  integration_type: string;
  is_new_contact: boolean;
  has_ai_agent: boolean;
  // Command Palette tracking
  trigger: string;
  item: string;
  /** A key of `AccountUserPrefsEnum`; see useHomeCheckList.tsx. */
  checklist_step: string;
  /** Which flow produced an outcome an event has more than one producer for. */
  source: 'import' | 'template';
  error_reason: ErrorReason;
  modal_name: string;
  action: SegmentAction;
  time_visible_seconds: number;
  time_elapsed_seconds: number;
  exit_reason: 'navigation' | 'pagehide' | 'hidden';
  was_capped: boolean;
  query_length: number;
  results_count: number;
  entity_type: string;
  entity_id: string;
  // Campaign tracking
  campaign_id: string;
  campaign_ids: string[];
  selected_count: number;
  campaign_type: string;
  channel_type: string;
  has_fallback: boolean;
  has_audience: boolean;
  autonomous_actions: boolean;
  is_scheduled: boolean;
  subscribers_count: number;
  status_filter: string;
  filter_type: string;
  filter_value: string;
  direction: string;
  // Simulations tracking
  run_type: string;
  // Authentication tracking
  enabled: boolean;
  // Analytics pages instrumentation
  page: PageName;
  report_id: string;
  // Sent on every page view and event by `globalContext` in src/client/segment/segment.ts
  moveo_region: string;
  is_internal: boolean;
  account_id: string;
  plan_id: string;
  role: UserRole;
  is_iframe: boolean;
  desk_id: string;
  view_id: string;
  rule_id: string;
  simulation_id: string;
  run_id: string;
  view_type: string;
  // Page-view taxonomy (see src/client/segment/pageTaxonomy.ts). On feature
  // events these come from the current-page store in segment.ts, so pass them
  // explicitly only to override.
  page_name: PageName;
  page_section: PageSection;
  page_type: PageType;
  path_template: string;
  resource_id: string;
  // `Page Section Exited` only. The page the visit started on, which is not the
  // page it ends on whenever the user moved around inside the section — that one
  // arrives as `page_name` / `path_template` from the page context.
  entry_page_name: PageName;
  entry_path_template: string;
}>;
